

import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import Icon from "../../../components/AppIcon";
import MultiSelect from "../../../components/ui/MultiSelect";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import API_BASE_URL from "../../../services/api";
import axiosInstance from "../../../utils/axiosInstance";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

// ---------- helpers ----------
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const round2 = (n) => +Number(n || 0).toFixed(2);

const parseDiscountInput = (raw) => {
  const text = String(raw ?? "").trim();
  if (!text) return null;

  if (text.endsWith("%")) {
    const num = Number(text.slice(0, -1).trim());
    if (!Number.isFinite(num)) return null;
    return { mode: "PERCENT", value: num };
  }

  const num = Number(text);
  if (!Number.isFinite(num)) return null;
  return { mode: "AMOUNT", value: num };
};

const parseCSV = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const toCSV = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).join(", ") : "");

const getTypeDisplayName = (t) => {
  const v = String(t || "").toUpperCase();
  if (v === "PATHOLOGY") return "Pathology";
  if (v === "RADIOLOGY") return "Radiology";
  return v || "-";
};

const mapDepartmentTypeToTestType = (deptType) => {
  const v = String(deptType || "").toUpperCase();
  if (v.includes("RADIO")) return "RADIOLOGY";
  if (v.includes("PATH")) return "PATHOLOGY";
  return "";
};

const TestPackageForm = ({
  formData,
  onChange,
  onTestTypeChange,
  type = "test",
  categories = [],
  subCategories = [],
  tests = [],
  testNames = [],
}) => {

  console.log("categories",categories)
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // ✅ user can type 10% or 150
  const [discountInput, setDiscountInput] = useState(
    formData?.discountInput ??
      (formData?.discount
        ? `${formData.discount}%`
        : formData?.discountAmount
        ? String(formData.discountAmount)
        : "")
  );

  // ✅ Selected tests UX state
  const [selectedSearch, setSelectedSearch] = useState("");
  const [hideSelectedFromDropdown, setHideSelectedFromDropdown] = useState(true);

  // ✅ Departments
  const [departments, setDepartments] = useState([]);

  // ✅ VITAL_ORGAN categories (fetched)
  const [vitalOrganCategories, setVitalOrganCategories] = useState([]);
  const [vitalCatsLoading, setVitalCatsLoading] = useState(false);

  // keep local discount input synced (edit mode)
  useEffect(() => {
    if (formData?.discountInput !== undefined) {
      setDiscountInput(formData.discountInput || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.discountInput]);

  // cleanup object url
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  // ✅ Fetch departments on mount
  const fetchDepartments = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/departments`);
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.items)
        ? res.data.items
        : res.data?.data || res.data?.departments || [];

      const active = (list || []).filter((d) => d?.isActive !== false);
      setDepartments(active);

      // ✅ Create mode default: set department + auto testType if empty
      if (type === "test") {
        if (!formData?.departmentItemId && active.length) {
          const first = active[0];
          const nextTestType =
            mapDepartmentTypeToTestType(first?.type) || formData?.testType || "";

          if (nextTestType && onTestTypeChange) onTestTypeChange(nextTestType);

          onChange({
            ...formData,
            departmentItemId: String(first?.id),
            ...(nextTestType
              ? {
                  testType: nextTestType,
                  categoryId: "",
                  subCategoryId: "",
                  otherCategoryIds: [],
                }
              : {}),
          });
        } else if (formData?.departmentItemId && !formData?.testType) {
          const dep = active.find((d) => String(d?.id) === String(formData.departmentItemId));
          const derived = mapDepartmentTypeToTestType(dep?.type);

          if (derived) {
            if (onTestTypeChange) onTestTypeChange(derived);
            onChange({
              ...formData,
              testType: derived,
              categoryId: "",
              subCategoryId: "",
              otherCategoryIds: [],
            });
          }
        }
      }
    } catch (e) {
      console.error("Error fetching departments:", e);
    }
  }, [formData, onChange, onTestTypeChange, type]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // ✅ Fetch ONLY VITAL_ORGAN categories on mount
  const fetchVitalOrganCategories = useCallback(async () => {
    try {
      setVitalCatsLoading(true);

      const res = await axiosInstance.get(`${API_BASE_URL}/api/categories/type`, {
        params: { type: "VITAL_ORGAN" },
      });

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.items)
        ? res.data.items
        : Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data?.categories)
        ? res.data.categories
        : [];

      const active = (list || []).filter((c) => c?.isActive !== false);

      setVitalOrganCategories(active);
    } catch (e) {
      console.error("Failed to fetch VITAL_ORGAN categories:", e);
      setVitalOrganCategories([]);
    } finally {
      setVitalCatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVitalOrganCategories();
  }, [fetchVitalOrganCategories]);

  // ✅ REMOVED VITAL_ORGAN FROM TEST TYPE
  const testTypeOptions = [
    { value: "PATHOLOGY", label: "Pathology" },
    { value: "RADIOLOGY", label: "Radiology" },
  ];

  const genderOptions = [
    { value: "", label: "Select Gender" },
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "both", label: "Both" },
  ];

  const reportUnitOptions = [
    { value: "mins", label: "Mins" },
    { value: "hours", label: "Hours" },
    { value: "days", label: "Days" },
  ];

  const sampleRequiredOptions = [
    { value: "Blood Sample", label: "Blood Sample" },
    { value: "Urine Sample", label: "Urine Sample" },
    { value: "Stool Sample", label: "Stool Sample" },
    { value: "Saliva Sample", label: "Saliva Sample" },
    { value: "Swab Sample", label: "Swab Sample" },
  ];

  const subCatsForCategory = useMemo(() => {
    return subCategories.filter((s) => Number(s.catId) === Number(formData.categoryId));
  }, [subCategories, formData.categoryId]);

  // ✅ SampleRequired UI value (array) derived from stored string
  const sampleRequiredValue = useMemo(
    () => parseCSV(formData.sampleRequired),
    [formData.sampleRequired]
  );

  // ✅ Other categories (Multi) value — keep as STRING IDs always
  const otherCategoriesValue = useMemo(() => {
    return Array.isArray(formData.otherCategoryIds) ? formData.otherCategoryIds.map(String) : [];
  }, [formData.otherCategoryIds]);

  // ✅ selected tests set (for hiding from dropdown)
  const selectedTestIdSet = useMemo(() => {
    return new Set((formData.selectedTests || []).map(String));
  }, [formData.selectedTests]);

  // ✅ map selected test ids -> test objects (for selected list)
  const selectedTestObjects = useMemo(() => {
    const map = new Map((testNames || []).map((t) => [String(t.id), t]));
    return (formData.selectedTests || []).map((id) => map.get(String(id))).filter(Boolean);
  }, [formData.selectedTests, testNames]);

  const filteredSelectedTestObjects = useMemo(() => {
    const q = selectedSearch.trim().toLowerCase();
    if (!q) return selectedTestObjects;
    return selectedTestObjects.filter((t) => String(t.name || "").toLowerCase().includes(q));
  }, [selectedTestObjects, selectedSearch]);

  // ✅ Other Categories options from API (VITAL_ORGAN only)
  const otherCategoryOptions = useMemo(() => {
    const primary = String(formData.categoryId || "");
    return (vitalOrganCategories || [])
      .filter((c) => String(c.id) !== primary)
      .map((c) => ({ value: String(c.id), label: c.name }));
  }, [vitalOrganCategories, formData.categoryId]);

  // compute discount fields from actualPrice + discountInput
  const computeDiscount = (next) => {
    const actual = Number(next.actualPrice || 0);

    if (!Number.isFinite(actual) || actual <= 0) {
      return {
        ...next,
        discount: 0,
        discountAmount: 0,
        offerPrice: "",
      };
    }

    const parsed = parseDiscountInput(next.discountInput);

    if (!parsed) {
      return {
        ...next,
        discount: 0,
        discountAmount: 0,
        offerPrice: round2(actual),
      };
    }

    let discountAmount = 0;
    let percent = 0;

    if (parsed.mode === "PERCENT") {
      percent = clamp(parsed.value, 0, 100);
      discountAmount = round2(actual * (percent / 100));
    } else {
      discountAmount = clamp(round2(parsed.value), 0, round2(actual));
      percent = round2((discountAmount / actual) * 100);
    }

    const offerPrice = round2(actual - discountAmount);

    return {
      ...next,
      discount: percent, // ✅ store % always
      discountAmount,
      offerPrice,
    };
  };

  const handleChange = (e) => {
    const { name, value, type: inputType, files, checked } = e.target;

    try {
      // image
      if (inputType === "file") {
        const file = files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
          alert("Please select an image file");
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          alert("Image size should be less than 5MB");
          return;
        }

        // revoke old preview
        if (imagePreview) URL.revokeObjectURL(imagePreview);

        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);

        onChange({
          ...formData,
          imageFile: file,
          imgUrl: previewUrl,
          removeImage: false,
        });
        return;
      }

      const next = {
        ...formData,
        [name]:
          inputType === "checkbox"
            ? checked
            : inputType === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
      };

      if (name === "actualPrice") {
        onChange(computeDiscount(next));
      } else {
        onChange(next);
      }
    } catch (error) {
      console.error("Form change error:", error);
    }
  };

  const handleDiscountInputChange = (e) => {
    const raw = e.target.value;
    setDiscountInput(raw);

    const next = {
      ...formData,
      discountInput: raw,
    };

    onChange(computeDiscount(next));
  };

  // ✅ SelectedTests must remain STRING IDs
  const handleSelectedTestsChange = (selectedValues) => {
    const next = (selectedValues || []).map(String);
    onChange({
      ...formData,
      selectedTests: next,
      noOfParameter: String(next.length),
    });
  };

  const handleRemoveSelectedTest = (removeId) => {
    const next = (formData.selectedTests || []).filter((id) => String(id) !== String(removeId));
    handleSelectedTestsChange(next);
  };

  const handleClearAllSelectedTests = () => {
    handleSelectedTestsChange([]);
  };

  // ✅ SampleRequired MultiSelect -> store as one string
  const handleSampleRequiredMultiChange = (selectedValues) => {
    onChange({
      ...formData,
      sampleRequired: toCSV(selectedValues),
    });
  };

  // ✅ Other Categories MultiSelect (STRING IDs)
  const handleOtherCategoriesChange = (selectedValues) => {
    const primary = String(formData.categoryId || "");
    const cleaned = (selectedValues || [])
      .map(String)
      .filter(Boolean)
      .filter((x) => x !== primary);

    onChange({
      ...formData,
      otherCategoryIds: cleaned,
    });
  };

  const handleRemoveImage = () => {
    try {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImagePreview(null);

      onChange({
        ...formData,
        imageFile: null,
        imgUrl: "",
        removeImage: true,
      });

      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e) {
      console.error("Remove image error:", e);
    }
  };

  // ✅ Department change auto-sets testType
  const handleDepartmentChange = (deptId) => {
    try {
      const dep = departments.find((d) => String(d?.id) === String(deptId));
      const derivedTestType = mapDepartmentTypeToTestType(dep?.type);

      if (derivedTestType && onTestTypeChange) onTestTypeChange(derivedTestType);

      onChange({
        ...formData,
        departmentItemId: deptId,
        ...(derivedTestType
          ? {
              testType: derivedTestType,
              categoryId: "",
              subCategoryId: "",
              otherCategoryIds: [],
            }
          : {}),
      });
    } catch (e) {
      console.error("Department change error:", e);
    }
  };

  const disableCategory = type === "test" && !formData.testType;
  const disableSubCategory = !formData.categoryId;

  return (
    <div className="space-y-4">
      {/* ✅ Image only for PACKAGE (Health Checkup) */}
      {type === "package" && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Image</label>

          <div className="flex items-center gap-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 transition relative overflow-hidden"
            >
              {imagePreview || formData.imgUrl ? (
                <img
                  src={imagePreview || formData.imgUrl}
                  alt="Preview"
                  className="h-full w-full object-cover rounded-lg"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-400">
                  <Icon name="Image" size={20} />
                  <span className="text-[10px] mt-1">Select Image</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Choose Image
              </button>

              {(imagePreview || formData.imgUrl) && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-xs px-3 py-1.5 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleChange}
            className="hidden"
          />

          <p className="text-[10px] text-gray-500 mt-1">JPG, PNG, WEBP • Max 300kb</p>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name || ""}
            onChange={handleChange}
            required
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Sort Order */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Sort Order</label>
          <input
            type="number"
            name="sortOrder"
            value={formData.sortOrder ?? 0}
            onChange={handleChange}
            min="0"
            step="1"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <p className="text-[10px] text-gray-500 mt-1">Lower number shows first (0 = top).</p>
        </div>

        {/* alsoKnowAs */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">alsoKnowAs</label>
          <input
            type="text"
            name="alsoKnowAs"
            value={formData.alsoKnowAs || ""}
            onChange={handleChange}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
          <select
            name="gender"
            value={formData.gender || ""}
            onChange={handleChange}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="both">Both</option>
          </select>
        </div>

        {/* Package category */}
        {type === "package" && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
            <select
              name="categoryId"
              value={formData.categoryId || ""}
              onChange={handleChange}
              required
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} - {c.type}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Test fields */}
        {type === "test" && (
          <>
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Test Type *</label>
              <select
                name="testType"
                value={formData.testType || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (onTestTypeChange) onTestTypeChange(value);
                  onChange({
                    ...formData,
                    testType: value,
                    categoryId: "",
                    subCategoryId: "",
                    otherCategoryIds: [],
                  });
                }}
                required
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Test Type</option>
                {testTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Department *</label>
              <select
                value={formData.departmentItemId || ""}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select Department</option>
                {departments.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.name} ({getTypeDisplayName(d.type)})
                  </option>
                ))}
              </select>
            
            </div>

         

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
              <select
                name="categoryId"
                value={formData.categoryId || ""}
                onChange={(e) =>
                  onChange({
                    ...formData,
                    categoryId: e.target.value,
                    subCategoryId: "",
                    otherCategoryIds: (formData.otherCategoryIds || []).filter(
                      (x) => String(x) !== String(e.target.value)
                    ),
                  })
                }
                required
                disabled={disableCategory}
                className={`w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  disableCategory
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300"
                }`}
              >
                <option value="">
                  {disableCategory ? "Select Test Type first" : "Select Category"}
                </option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ Other Categories Multi (from API: VITAL_ORGAN) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Other Categories (Vital Organ only)
              </label>

              <MultiSelect
                options={otherCategoryOptions}
                value={otherCategoriesValue}
                onChange={handleOtherCategoriesChange}
                placeholder={
                  vitalCatsLoading ? "Loading vital organ categories..." : "Select Vital Organ category(s)..."
                }
                className="w-full"
              />

            
            </div>

            {/* Sub Category */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sub Category</label>
              <select
                name="subCategoryId"
                value={formData.subCategoryId || ""}
                onChange={handleChange}
                disabled={disableSubCategory}
                className={`w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  disableSubCategory
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300"
                }`}
              >
                <option value="">
                  {disableSubCategory ? "Select Category first" : "Select Sub Category"}
                </option>
                {subCatsForCategory.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Actual Price */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Actual Price *</label>
          <input
            type="number"
            name="actualPrice"
            value={formData.actualPrice ?? ""}
            onChange={handleChange}
            required
            min="0"
            step="0.01"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Discount */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Discount (type "10%" or "150")
          </label>
          <input
            type="text"
            value={discountInput}
            onChange={handleDiscountInputChange}
            placeholder="e.g. 10% or 150"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            If you add %, it will be treated as percentage. Otherwise it’s amount (₹).
          </p>
        </div>

        {/* Discount Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Discount Amount (₹)</label>
          <input
            type="number"
            value={formData.discountAmount || 0}
            readOnly
            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-gray-50"
          />
        </div>

        {/* Offer Price */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Offer Price (₹)</label>
          <input
            type="number"
            value={formData.offerPrice ?? ""}
            readOnly
            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-gray-50"
          />
        </div>

        {/* Report Unit */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Report Unit</label>
          <select
            name="reportUnit"
            value={formData.reportUnit || "hours"}
            onChange={handleChange}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="mins">Mins</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
          </select>
        </div>

        {/* Report Within */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Report Within *</label>
          <input
            type="number"
            name="reportWithin"
            value={formData.reportWithin ?? ""}
            onChange={handleChange}
            required
            min="1"
            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Sample Required + Preparations */}
      {(type === "test" || type === "package") && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Sample Required (Multi)
            </label>

            <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
              <div className="px-2 py-1">
                <MultiSelect
                  options={[
                    { value: "Blood Sample", label: "Blood Sample" },
                    { value: "Urine Sample", label: "Urine Sample" },
                    { value: "Stool Sample", label: "Stool Sample" },
                    { value: "Saliva Sample", label: "Saliva Sample" },
                    { value: "Swab Sample", label: "Swab Sample" },
                  ]}
                  value={sampleRequiredValue}
                  onChange={handleSampleRequiredMultiChange}
                  placeholder="Select sample(s)..."
                  className="w-full text-xs"
                />
              </div>
            </div>

            <p className="text-[10px] text-gray-500 mt-1">
              Saved as string: <span className="font-medium">{formData.sampleRequired || "-"}</span>
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Preparations</label>
            <input
              type="text"
              name="preparations"
              value={formData.preparations || ""}
              onChange={handleChange}
              placeholder="e.g., Fasting required, No special preparation"
              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Package Tests MultiSelect */}
      {type === "package" && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-xs font-medium text-gray-700">
              Select Tests for Package *
            </label>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideSelectedFromDropdown}
                  onChange={(e) => setHideSelectedFromDropdown(e.target.checked)}
                />
                Hide selected
              </label>

              <button
                type="button"
                onClick={handleClearAllSelectedTests}
                disabled={(formData.selectedTests || []).length === 0}
                className="text-[10px] font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                Clear all
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
            <div className="px-2 py-1">
              <MultiSelect
                options={(testNames || [])
                  .filter((t) => (hideSelectedFromDropdown ? !selectedTestIdSet.has(String(t.id)) : true))
                  .map((t) => ({
                    value: String(t.id),
                    label: t.name,
                    price: t.offerPrice || t.actualPrice,
                  }))}
                value={(formData.selectedTests || []).map(String)}
                onChange={handleSelectedTestsChange}
                placeholder="Search and select tests..."
                renderOption={(option) => (
                  <div className="flex justify-between items-center">
                    <span className="truncate">{option.label}</span>
                    <span className="text-xs text-gray-500">₹{option.price}</span>
                  </div>
                )}
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-gray-300 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-3 py-2 bg-gray-50 rounded-t-lg">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-700 font-semibold">
                  Selected: {formData.selectedTests?.length || 0} tests • Parameters:{" "}
                  {formData.noOfParameter || 0}
                </p>

                <input
                  value={selectedSearch}
                  onChange={(e) => setSelectedSearch(e.target.value)}
                  placeholder="Search selected..."
                  className="w-44 px-2 py-1 text-[10px] border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="p-2">
              {(formData.selectedTests || []).length === 0 ? (
                <p className="text-[10px] text-gray-500">No tests selected yet.</p>
              ) : (
                <div className="max-h-44 overflow-auto space-y-1.5">
                  {filteredSelectedTestObjects.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between gap-2 rounded-md bg-gray-50 border border-gray-200 px-2 py-1.5"
                    >
                      <div className="min-w-0">
                        <p className="text-xs text-gray-800 truncate">{t.name}</p>
                        <p className="text-[10px] text-gray-500">
                          ₹{t.offerPrice || t.actualPrice || 0}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveSelectedTest(t.id)}
                        className="text-[10px] font-bold text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spotlight */}
      <div className="flex items-center gap-2 mt-2">
        <input
          id="spotlight"
          type="checkbox"
          name="spotlight"
          checked={!!formData.spotlight}
          onChange={handleChange}
        />
        <label htmlFor="spotlight" className="text-xs font-medium text-gray-700">
          Show in Spotlight
        </label>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={formData.description || ""}
          onChange={handleChange}
          rows={3}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Detailed description of the test/package..."
        />
      </div>

      {/* Features */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Features</label>
        <div className="border border-gray-300 rounded-md overflow-hidden">
          <ReactQuill
            theme="snow"
            value={formData.features || ""}
            onChange={(value) => onChange({ ...formData, features: value })}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Enter test/package features..."
          />
        </div>
        <p className="text-[10px] text-gray-500 mt-1">
          You can add bullet points, highlights, and key features.
        </p>
      </div>
    </div>
  );
};

export default TestPackageForm;
